import type { CookieConsentState } from '@/utils/cookieConsentStorage';
import { isWidget } from '@/utils/isWidget.web';

import type {
  DataRefreshPayload,
  WidgetMessageHandler,
  WidgetMessageName,
} from './widgetMessageContract';
import { WIDGET_MESSAGES } from './widgetMessageContract';

export * from './widgetMessageContract';

const PARENT_CONSENT_TIMEOUT_MS = 1000;

const PARENT_ONLY_MESSAGES: ReadonlySet<string> = new Set([
  WIDGET_MESSAGES.widgetShown,
  WIDGET_MESSAGES.appInitialData,
  WIDGET_MESSAGES.passConsent,
]);

const handlersByName = new Map<string, Set<WidgetMessageHandler>>();
let isListening = false;

const isCallMessage = (
  data: unknown,
): data is { call: string; value?: unknown } =>
  typeof data === 'object' &&
  data !== null &&
  typeof (data as { call?: unknown }).call === 'string';

const onMessage = (event: MessageEvent<unknown>): void => {
  if (!isCallMessage(event.data)) return;
  const { call, value } = event.data;
  if (PARENT_ONLY_MESSAGES.has(call) && event.source !== window.parent) return;
  handlersByName.get(call)?.forEach((handler) => handler(value));
};

const ensureListening = (): void => {
  if (isListening) return;
  window.addEventListener('message', onMessage);
  isListening = true;
};

const postToParent = (message: unknown): void => {
  if (!isWidget) return;
  window.parent.postMessage(message, '*');
};

export const postPlain = (name: WidgetMessageName): void => postToParent(name);

export const postCall = (name: WidgetMessageName, value: unknown): void =>
  postToParent({ call: name, value });

export const postDataRefresh = (payload: DataRefreshPayload): void =>
  postToParent(JSON.stringify({ type: WIDGET_MESSAGES.dataRefresh, payload }));

export const subscribe = (
  name: WidgetMessageName,
  handler: WidgetMessageHandler,
): (() => void) => {
  ensureListening();
  let handlers = handlersByName.get(name);
  if (!handlers) {
    handlers = new Set();
    handlersByName.set(name, handlers);
  }
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
};

export const waitForParentMessage = <T>(
  name: WidgetMessageName,
  timeoutMs: number,
): Promise<T | null> =>
  new Promise((resolve) => {
    let unsubscribe = (): void => {};
    const timer = setTimeout(() => {
      unsubscribe();
      resolve(null);
    }, timeoutMs);
    unsubscribe = subscribe(name, (value) => {
      clearTimeout(timer);
      unsubscribe();
      resolve(value as T);
    });
  });

export const requestParentConsent = (): Promise<string | null> => {
  if (!isWidget) return Promise.resolve(null);
  const reply = waitForParentMessage<unknown>(
    WIDGET_MESSAGES.passConsent,
    PARENT_CONSENT_TIMEOUT_MS,
  );
  postPlain(WIDGET_MESSAGES.requestConsent);
  return reply.then((value) => (typeof value === 'string' ? value : null));
};

export const pushConsentToParent = (state: CookieConsentState): void =>
  postCall(WIDGET_MESSAGES.setConsent, state);
